// Full-screen viewer for a source, step or output. The left panel shows its
// settings or its details (metadata and a column profile). The right side
// shows its data, a diff against its input, and issues. Opens when a card or
// list row is clicked; the header switches between nodes without closing.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Segmented, Select, Tooltip } from "antd";
import { ChevronLeft, ChevronRight, CircleAlert, Crosshair, PanelLeftClose, PanelLeftOpen, Search, Sparkles, TriangleAlert, X } from "lucide-react";
import { getStepDefinition, stepInputs, tableLabel } from "../../../engine";
import { dataHost } from "../data/dataHost";
import DataTable from "../shared/DataTable";
import { displayValue, formatCount } from "../shared/display";
import { useBuilder } from "./BuilderContext";
import { buildStepGroups } from "./trail/graph";
import OutputInspector from "./inspector/OutputInspector";
import SourceInspector from "./inspector/SourceInspector";
import StepInspector from "./inspector/StepInspector";

const CHIPS = [
  { key: "rows", label: "rows" },
  { key: "columns", label: "columns" },
  { key: "nulls", label: "null", filter: "nulls" },
  { key: "empties", label: "empty", filter: "empties" },
  { key: "duplicates", label: "duplicates", filter: "duplicates" },
  { key: "warnings", label: "warnings", tone: "amber", tab: "issues" },
  { key: "typeErrors", label: "type errors", tone: "red", filter: "typeErrors" },
];

// Every node in flow order, for the switcher in the header.
const useNodes = (workflow) =>
  useMemo(
    () => [
      ...[...workflow.sources, ...workflow.steps].map((item) => ({ value: item.id, label: tableLabel(workflow, item.id) })),
      ...workflow.outputs.map((output) => ({ value: output.id, label: `Output · ${output.fileName}` })),
    ],
    [workflow],
  );

// Loads a value from the data worker whenever `key` changes. While a new
// value loads, the old one stays if `keepKey` is unchanged (same table, new
// run), so nothing flickers; switching tables clears it. Results with
// { stale: true } (overtaken by a newer request) are ignored.
const useWorkerValue = (load, key, keepKey) => {
  const [state, setState] = useState({ keepKey: null, key: null, value: null });
  useEffect(() => {
    let live = true;
    const request = load();
    if (!request) {
      setState({ keepKey, key, value: null });
      return undefined;
    }
    request
      .then((value) => live && !value?.stale && setState({ keepKey, key, value }))
      .catch(() => live && setState({ keepKey, key, value: null }));
    return () => {
      live = false;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state.keepKey === keepKey ? { value: state.value, current: state.key === key } : { value: null, current: false };
};

// What the viewer shows for node `id`. `ref` points at its table in the data
// worker ({ id } or { output }); `info` is { columns, rowCount } when known.
const useSelection = (id) => {
  const { state, run, outputs } = useBuilder();
  const { workflow } = state;
  return useMemo(() => {
    const source = workflow.sources.find((s) => s.id === id);
    const output = workflow.outputs.find((o) => o.id === id);
    const stepIndex = workflow.steps.findIndex((s) => s.id === id);
    const step = workflow.steps[stepIndex];
    if (!source && !output && !step) return null;

    if (output) {
      const file = outputs.files.find((f) => f.id === output.id);
      const sheet = file?.sheets[0];
      return {
        kind: "output",
        item: output,
        title: output.fileName,
        crumb: "Output",
        ref: sheet ? { output } : null,
        info: null, // the written columns are read from the worker
        empty: "This output isn't ready yet. Check its settings.",
        warnings: [],
        errors: outputs.errorsById?.[output.id] || [],
        facts: [
          ["File", `${output.fileName}.${output.format}`],
          ["Format", output.format === "xlsx" ? "Excel" : "CSV"],
          ["Reads from", output.sheets.map((s) => tableLabel(workflow, s.from)).join(", ") || "Nothing yet"],
          ...(output.format === "xlsx" ? [["Sheets", output.sheets.map((s) => s.name).join(", ")]] : []),
          ["Status", file ? "Ready" : "Not ready"],
        ],
      };
    }
    if (source) {
      const file = state.files[source.id];
      return {
        kind: "source",
        item: source,
        title: source.label,
        crumb: source.id.replace("source", "Source "),
        ref: file && run?.tables[source.id] ? { id: source.id } : null,
        info: file ? run?.tables[source.id] : null,
        empty: "Upload a sample file for this source to see its data.",
        warnings: (run?.sourceIssues[source.id] || []).filter((i) => i.level !== "error").map((i) => i.message),
        errors: (run?.sourceIssues[source.id] || []).filter((i) => i.level === "error").map((i) => i.message),
        facts: [
          ["File", source.originalName || "–"],
          ...(source.sheetName ? [["Sheet", source.sheetName]] : []),
          ["Header row", source.hasHeader ? source.headerRow : "None"],
          ["Match columns by", source.matchBy === "position" ? "Position" : "Header"],
          ["Expected columns", source.columns.length],
          ["Sample file", file ? file.fileName : "Not uploaded"],
        ],
      };
    }
    const definition = getStepDefinition(step.type);
    const result = run?.results.find((r) => r.stepId === step.id);
    const group = buildStepGroups(workflow).find((g) => g.steps.some((s) => s.step.id === step.id));
    const inputIds = stepInputs(workflow, stepIndex);
    const failed = run?.error?.stepId === step.id;
    return {
      kind: "step",
      item: step,
      title: step.label || definition?.label || step.type,
      crumb: group && group.steps.length > 1 ? `Steps ${group.steps[0].index + 1}–${group.steps[group.steps.length - 1].index + 1}` : null,
      stepNumber: stepIndex + 1,
      category: definition?.category,
      description: definition?.description,
      ref: run?.tables[step.id] ? { id: step.id } : null,
      info: run?.tables[step.id],
      inputId: definition?.inputs.max === 1 && run?.tables[inputIds[0]] ? inputIds[0] : null,
      inputLabel: tableLabel(workflow, inputIds[0]),
      empty: failed ? "This step has an error — see Issues." : "Not run yet: fix the error in an earlier step first.",
      warnings: result?.warnings || [],
      errors: failed ? [result?.error] : [],
      facts: [
        ["Function", definition?.label || step.type],
        ["Category", definition?.category || "–"],
        ["Reads from", inputIds.map((inputId) => tableLabel(workflow, inputId)).join(", ") || "Not connected"],
        ["Rows", result?.rowsOut !== undefined ? `${formatCount(result.rowsIn)} → ${formatCount(result.rowsOut)}` : "–"],
        ["Run time", result?.durationMs !== undefined ? `${result.durationMs.toFixed(1)} ms` : "–"],
        ["Status", step.disabled ? "Disabled" : failed ? "Error" : result?.warnings?.length ? "Warnings" : result ? "OK" : "Not run"],
      ],
    };
  }, [workflow, id, state.files, run, outputs]);
};

// Metadata of the node and a profile of each column.
const DetailsView = ({ selection, profile }) => (
  <div className="space-y-5">
    {selection.description && <p className="text-xs text-gray-500">{selection.description}</p>}
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
      {selection.facts.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="text-gray-500">{label}</dt>
          <dd className="min-w-0 break-words font-medium text-gray-900">{value}</dd>
        </Fragment>
      ))}
    </dl>
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Columns</div>
      {!profile ? (
        <div className="text-xs text-gray-400">No data yet.</div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-1 font-medium">Column</th>
              <th className="pb-1 font-medium">Type</th>
              <th className="pb-1 text-right font-medium">Distinct</th>
              <th className="pb-1 text-right font-medium">Blank</th>
              <th className="pb-1 text-right font-medium">Invalid</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {profile.columns.map((column, index) => (
              <tr key={column.name} className="border-t border-gray-100">
                <td className="max-w-0 truncate py-1 pr-2 font-sans text-gray-900">
                  <span className="mr-1 rounded bg-gray-800 px-1 font-mono text-[10px] text-white">{column.letter}</span>
                  {selection.columns[index]?.label || column.name}
                </td>
                <td className="py-1 pr-2 text-gray-600">{column.type}</td>
                <td className="py-1 text-right">{column.distinct.toLocaleString()}</td>
                <td className="py-1 text-right">{(column.null + column.empty).toLocaleString()}</td>
                <td className={`py-1 text-right ${column.mismatch ? "text-red-600" : ""}`}>{column.mismatch.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// Added, removed and changed columns compared with the step's input, worked
// out in the data worker; changed cells come with the rows.
const DiffView = ({ selection, version, profile, inputLabel }) => {
  const refKey = JSON.stringify(selection.ref);
  const { value: summary } = useWorkerValue(() => dataHost.getDiff("editor", selection.ref, selection.inputId), `${refKey}|${version}|${selection.inputId}`, refKey);
  const highlight = useMemo(() => new Set(summary ? [...summary.added, ...summary.changed] : []), [summary]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-gray-100 px-3 py-2 font-mono text-xs font-medium text-gray-700">
        {!summary ? (
          <span className="text-gray-400">Comparing with {inputLabel}…</span>
        ) : (
          <>
            <span>
              rows {summary.rowsIn.toLocaleString()} → {summary.rowsOut.toLocaleString()} <span className="text-gray-400">(vs {inputLabel})</span>
            </span>
            {summary.added.length > 0 && <span className="text-teal-700">+ added: {summary.added.join(", ")}</span>}
            {summary.removed.length > 0 && <span className="text-red-600">− removed: {summary.removed.join(", ")}</span>}
            {summary.changed.length > 0 && <span className="text-amber-700">~ changed: {summary.changed.join(", ")}</span>}
            {!summary.added.length && !summary.removed.length && !summary.changed.length && <span>No column changes</span>}
          </>
        )}
      </div>
      <div className="min-h-0 flex-1">
        <DataTable
          tableRef={selection.ref}
          version={version}
          columns={selection.columns}
          total={selection.rowCount}
          profile={profile}
          highlightColumns={highlight}
          diffWith={summary?.sameRows ? selection.inputId : undefined}
        />
      </div>
    </div>
  );
};

const IssuesView = ({ selection, profile, onJump }) => {
  const byColumn = useMemo(() => {
    const groups = new Map();
    profile?.issues.forEach((issue) => {
      if (!groups.has(issue.column)) groups.set(issue.column, []);
      groups.get(issue.column).push(issue);
    });
    return [...groups.entries()];
  }, [profile]);
  const columnInfo = (name) => profile.columns.find((c) => c.name === name);
  const nothing = !selection.errors.length && !selection.warnings.length && !byColumn.length;

  const fixWithAi = (
    <Tooltip title="AI fixes are coming soon">
      <Button size="small" disabled icon={<Sparkles size={12} />}>
        Fix with AI
      </Button>
    </Tooltip>
  );

  return (
    <div className="h-full space-y-2 overflow-y-auto p-3">
      {nothing && <div className="py-10 text-center text-sm text-gray-400">No issues found 🎉</div>}
      {selection.errors.map((error) => (
        <div key={error} className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          <CircleAlert size={14} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          {fixWithAi}
        </div>
      ))}
      {selection.warnings.map((warning) => (
        <div key={warning} className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          <TriangleAlert size={14} className="mt-0.5 shrink-0" />
          <span className="flex-1">{warning}</span>
          {fixWithAi}
        </div>
      ))}
      {byColumn.map(([column, issues]) => {
        const info = columnInfo(column);
        return (
          <div key={column} className="rounded-md border border-gray-200 p-2 text-[13px]">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-semibold text-gray-800">
                <span className="mr-1 rounded bg-gray-800 px-1 text-[10px] text-white">{info?.letter}</span>
                {column}: {info?.mismatch.toLocaleString()} value(s) aren't a valid {info?.type}
              </span>
              {fixWithAi}
            </div>
            <div className="space-y-1">
              {issues.slice(0, 5).map((issue) => (
                <div key={issue.row} className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 font-mono">
                  <span>
                    row {issue.row + 1}: <span className="text-red-600">{displayValue(issue.value).text}</span>
                  </span>
                  <button type="button" className="flex items-center gap-1 text-teal-700 hover:underline" onClick={() => onJump(issue)}>
                    <Crosshair size={12} /> Jump to cell
                  </button>
                </div>
              ))}
              {issues.length > 5 && <div className="text-gray-400">+{issues.length - 5} more</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const NodeViewer = ({ open, onClose }) => {
  const { state, run, outputs, dispatch, select } = useBuilder();
  // Nothing is computed while the viewer is closed.
  const selection = useSelection(open ? state.selectedId : null);
  const nodes = useNodes(state.workflow);
  const [panel, setPanel] = useState("settings");
  const [panelOpen, setPanelOpen] = useState(true);
  const [tab, setTab] = useState("data");
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [jumpTo, setJumpTo] = useState(null);

  useEffect(() => {
    setFilter(null);
    setJumpTo(null);
  }, [selection?.item?.id]);

  // The table's rows, profile and filtered views live in the data worker.
  const version = run?.version;
  const refKey = selection?.ref ? JSON.stringify(selection.ref) : null;
  const { value: outputInfo } = useWorkerValue(
    () => (selection?.kind === "output" && selection.ref ? dataHost.describeTable("editor", selection.ref) : null),
    `${refKey}|${version}`,
    refKey,
  );
  const info = selection?.kind === "output" ? outputInfo : selection?.info;
  const table = selection?.ref && info ? { ...selection, columns: info.columns, rowCount: info.rowCount } : null;

  const warningCount = selection?.warnings.length ?? 0;
  const { value: profileValue } = useWorkerValue(
    () => (table ? dataHost.getProfile("editor", table.ref, { warnings: selection.warnings }) : null),
    `${refKey}|${version}|${warningCount}|${Boolean(table)}`,
    refKey,
  );
  const profile = table ? profileValue : null;

  // A source's rows above its data (header row included), shown greyed above it.
  const { value: leadingValue } = useWorkerValue(
    () => (table && selection.kind === "source" ? dataHost.getLeadingRows("editor", table.ref) : null),
    `${refKey}|${version}|${Boolean(table)}`,
    refKey,
  );
  const leadingRows = table && selection.kind === "source" ? leadingValue?.rows : null;

  // Search waits for a pause in typing; filtering happens in the worker.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const wantsView = Boolean(table && (filter || query));
  const { value: view, current: viewCurrent } = useWorkerValue(
    () => (wantsView ? dataHost.createView("editor", table.ref, { filter, search: query }) : null),
    `${refKey}|${version}|${filter}|${query}|${wantsView}`,
    refKey,
  );
  const lastView = useRef(null);
  useEffect(() => {
    if (lastView.current && lastView.current !== view?.viewId) dataHost.dropView(lastView.current).catch(() => {});
    lastView.current = view?.viewId ?? null;
  }, [view]);
  const shownView = wantsView ? view : null;
  const filtering = wantsView && !viewCurrent;

  const issueCount = selection ? selection.errors.length + selection.warnings.length + (profile?.counts.typeErrors || 0) : 0;
  // "Diff" only exists for single-input steps; fall back to the data.
  const activeTab = tab === "diff" && !selection?.inputId ? "data" : tab;
  const position = nodes.findIndex((node) => node.value === selection?.item.id);

  const settings =
    selection?.kind === "source" ? (
      <SourceInspector
        source={selection.item}
        file={state.files[selection.item.id]}
        issues={run?.sourceIssues[selection.item.id]}
        dispatch={dispatch}
        hasSteps={state.workflow.steps.length > 0}
      />
    ) : selection?.kind === "output" ? (
      <OutputInspector
        output={selection.item}
        workflow={state.workflow}
        run={run}
        ready={outputs.files.some((f) => f.id === selection.item.id)}
        errors={selection.errors}
        dispatch={dispatch}
      />
    ) : selection ? (
      <StepInspector key={selection.item.id} step={selection.item} workflow={state.workflow} run={run} dispatch={dispatch} />
    ) : null;

  return (
    <Modal
      open={open && Boolean(selection)}
      onCancel={onClose}
      footer={null}
      closable={false}
      // Below other dialogs (add step, confirmations) so they open on top.
      zIndex={900}
      wrapClassName="node-viewer"
      width="calc(100vw - 32px)"
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { padding: 0, height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", overflow: "hidden" },
        body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
      }}
    >
      {selection && (
        <>
          <header className="flex h-12 shrink-0 items-center gap-3 border-b border-gray-200 px-3">
            <Tooltip title={panelOpen ? "Hide settings" : "Show settings"}>
              <Button size="small" type="text" icon={panelOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />} onClick={() => setPanelOpen((v) => !v)} />
            </Tooltip>
            <div className="flex min-w-0 items-center gap-2 text-sm">
              {selection.crumb && <span className="text-gray-400">{selection.crumb} ›</span>}
              <span className="truncate font-semibold text-gray-900">
                {selection.stepNumber ? `Step ${selection.stepNumber} · ` : ""}
                {selection.title}
              </span>
              {selection.category && <span className="rounded border border-gray-200 px-1.5 text-[11px] text-gray-500">{selection.category}</span>}
              {selection.item.disabled && <span className="rounded bg-gray-100 px-1.5 text-[11px] text-gray-500">disabled</span>}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Segmented
                size="small"
                value={activeTab}
                onChange={setTab}
                options={[
                  { value: "data", label: selection.kind === "output" ? "Output file" : "Output data" },
                  ...(selection.inputId ? [{ value: "diff", label: "Diff vs input" }] : []),
                  { value: "issues", label: `Issues · ${issueCount}` },
                ]}
              />
              <div className="flex items-center gap-1 border-l border-gray-200 pl-2">
                <Tooltip title="Previous">
                  <Button size="small" type="text" icon={<ChevronLeft size={16} />} disabled={position <= 0} onClick={() => select(nodes[position - 1].value)} />
                </Tooltip>
                <Select size="small" className="!w-56" popupMatchSelectWidth={false} value={selection.item.id} options={nodes} onChange={select} />
                <Tooltip title="Next">
                  <Button size="small" type="text" icon={<ChevronRight size={16} />} disabled={position === -1 || position >= nodes.length - 1} onClick={() => select(nodes[position + 1].value)} />
                </Tooltip>
              </div>
              <Tooltip title="Close (Esc)">
                <Button size="small" type="text" icon={<X size={16} />} onClick={onClose} />
              </Tooltip>
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            {panelOpen && (
              <aside className="flex w-[400px] shrink-0 flex-col border-r border-gray-200">
                <div className="border-b border-gray-100 px-4 py-2">
                  <Segmented
                    block
                    size="small"
                    value={panel}
                    onChange={setPanel}
                    options={[
                      { value: "settings", label: "Settings" },
                      { value: "details", label: "Details" },
                    ]}
                  />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">{panel === "settings" ? settings : <DetailsView selection={{ ...selection, columns: table?.columns || [] }} profile={profile} />}</div>
              </aside>
            )}

            <div className="flex min-w-0 flex-1 flex-col">
              {activeTab === "data" && table && (
                <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3 py-2">
                  {CHIPS.map((chip) => {
                    // Rows and columns are known at once; the rest once the profile is ready.
                    const count = profile ? profile.counts[chip.key] : chip.key === "rows" ? table.rowCount : chip.key === "columns" ? table.columns.length : null;
                    const tone = count && chip.tone === "red" ? "bg-red-50 text-red-700 border-red-200" : count && chip.tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-gray-50 text-gray-700 border-gray-200";
                    const active = filter && filter === chip.filter;
                    const clickable = Boolean((chip.filter && count) || chip.tab);
                    return (
                      <button
                        type="button"
                        key={chip.key}
                        disabled={!clickable}
                        onClick={() => (chip.tab ? setTab(chip.tab) : setFilter(active ? null : chip.filter))}
                        className={`rounded-md border px-2.5 py-1 font-mono text-xs font-medium ${tone} ${active ? "ring-2 ring-teal-500" : ""} ${clickable ? "hover:brightness-95" : "cursor-default"}`}
                      >
                        {count === null ? "…" : count.toLocaleString()} {chip.label}
                      </button>
                    );
                  })}
                  <Input
                    size="small"
                    allowClear
                    className="ml-auto !w-52"
                    prefix={<Search size={12} className="text-gray-400" />}
                    placeholder="Search rows"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              )}

              <div className="min-h-0 flex-1">
                {activeTab === "issues" ? (
                  <IssuesView
                    selection={selection}
                    profile={profile}
                    onJump={(issue) => {
                      setFilter(null);
                      setTab("data");
                      setJumpTo({ ...issue, at: Date.now() });
                    }}
                  />
                ) : !table ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">{selection.ref && !info ? "Loading…" : selection.empty}</div>
                ) : activeTab === "diff" ? (
                  <DiffView selection={table} version={version} profile={profile} inputLabel={selection.inputLabel} />
                ) : (
                  <DataTable
                    tableRef={table.ref}
                    version={shownView && shownView.version !== version ? shownView.version : version}
                    columns={table.columns}
                    total={shownView ? shownView.total : table.rowCount}
                    viewId={shownView?.viewId}
                    profile={profile}
                    jumpTo={jumpTo}
                    leadingRows={leadingRows}
                  />
                )}
              </div>

              {activeTab === "data" && table && (
                <footer className="flex items-center gap-5 border-t border-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600">
                  <span>∅ null</span>
                  <span>"" empty string</span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-50 ring-1 ring-red-300" /> type mismatch
                  </span>
                  <span className="ml-auto">
                    {filtering ? "Filtering… · " : ""}
                    {(shownView ? shownView.total : table.rowCount).toLocaleString()} of {table.rowCount.toLocaleString()} rows · {table.columns.length} columns
                  </span>
                </footer>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default NodeViewer;
